/** @jsx jsx */
import { ReactNode } from 'react';
import { css, jsx } from '@emotion/react';
import { Link } from 'gatsby';
import { Box, xcss } from '@atlaskit/primitives';
import { CustomItem, CustomItemProps } from '@atlaskit/menu';
import { B100, N20 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import { HeadingIcon } from '@af/design-system-docs-ui';
import { CATEGORY_TITLES } from '../../utils/constants';

interface HeadingItemProps {
  children: ReactNode;
  indexName: string;
}

const backgroundMapping = {
  [CATEGORY_TITLES.COMPONENTS]: token(
    'color.background.accent.green.subtlest',
    '#DFFCF0',
  ),
  [CATEGORY_TITLES.TOKENS]: token(
    'color.background.accent.yellow.subtlest',
    '#FFF7D6',
  ),
  [CATEGORY_TITLES.FOUNDATIONS]: token(
    'color.background.accent.teal.subtlest',
    '#E3FAFC',
  ),
  [CATEGORY_TITLES.RESOURCES]: token(
    'color.background.accent.magenta.subtlest',
    '#FFECF8',
  ),
  [CATEGORY_TITLES.PATTERNS]: token(
    'color.background.accent.orange.subtlest',
    '#FFF4E5',
  ),
  [CATEGORY_TITLES.CONTENT]: token(
    'color.background.accent.red.subtlest',
    '#FFEDEB',
  ),
  [CATEGORY_TITLES.BRAND]: token(
    'color.background.accent.blue.subtlest',
    '#E9F2FF',
  ),
  [CATEGORY_TITLES.GET_STARTED]: token(
    'color.background.accent.purple.subtlest',
    '#F3F0FF',
  ),
};
const headingItemStyles = xcss({
  display: 'flex',
  flexDirection: 'row',
  paddingBlock: 'space.150',
  paddingInline: 'space.150',
  margin: `0 ${token('space.200', '16px')}`,
  fontSize: token('font.size.100', '14px'),
  borderRadius: 'border.radius.200',
  gap: 'space.100',
});

export const HeadingItem = ({ children, indexName }: HeadingItemProps) => (
  <CustomItem
    component={({ children }) => (
      <Box
        xcss={headingItemStyles}
        style={{
          backgroundColor: backgroundMapping[indexName],
        }}
      >
        <HeadingIcon heading={indexName} />
        {children}
      </Box>
    )}
  >
    {children}
  </CustomItem>
);

interface LinkItemProps extends CustomItemProps {
  to: string;
  children: ReactNode;
}

const linkItemStyles = css({
  // FIXME: !important use
  padding: `${token('space.200', '16px')} ${token(
    'space.300',
    '24px',
  )} !important`,
  color: 'inherit',
  outline: 'none',
  tabIndex: 0,
  textDecoration: 'none',
  '& p': {
    lineHeight: token('font.lineHeight.200', '20px'),
  },
  '&:hover': {
    color: 'inherit',
    textDecoration: 'none',
    backgroundColor: token('color.background.neutral.subtle.hovered', N20),
    outline: 'none',
  },
  '&:focus': {
    color: 'inherit',
    boxShadow: `0px 0px 0px 2px inset ${token('color.border.focused', B100)};`,
    textDecoration: 'none',
    outline: 'none',
  },
  '&: active': {
    textDecoration: 'none',
    outline: 'none',
    backgroundColor: token('color.background.neutral.subtle.pressed', N20),
  },
  em: {
    fontWeight: token('font.weight.bold', '700'),
    fontStyle: 'inherit',
  },
});

export const LinkItem = ({ to, children, ...props }: LinkItemProps) => (
  <CustomItem
    {...props}
    component={(props) => <Link css={linkItemStyles} to={to} {...props} />}
  >
    {children}
  </CustomItem>
);
